import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import { useNavigation } from '@/modules/core/composables/navigationComposable'
import { ButtonTypeEnum, ErrorDisplayTypeEnum, UserRoleEnum } from '@/modules/core/models/enums'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useToastStore } from '@/modules/core/stores/toastStore'
import { defineStore } from 'pinia'
import { ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import ENDPOINTS from '@/modules/core/constants/apiEndpoints'
import ServiceSchemaError from '@/modules/core/models/errors/ServiceSchemaError'

import Insurance from '@/modules/common/common/models/APP/Insurance'
import GetProductResponse from '@/modules/common/common/models/DXP/responses/GetProductResponse'
import RaterPriceResponse from '@/modules/common/common/models/MID/responses/RaterPriceResponse'
import ServiceError from '@/modules/core/models/errors/ServiceError'
import ModalButton from '@/modules/core/models/modalMessage/ModalButton'
import DXPService from '@/modules/core/services/DXPService'
import MiddlewareService from '@/modules/core/services/MiddlewareService'

import CommonMapper from '@/modules/common/common/mappers/index'
import CoverageBasic from '@/modules/common/common/models/APP/CoverageBasic'
import type FormPage from '@/modules/common/common/models/APP/FormPage'
import Insured from '@/modules/common/common/models/APP/Insured'
import type DXPProduct from '@/modules/common/common/models/DXP/Product'
import ProductId from '@/modules/common/common/models/DXP/ProductId'
import type RaterPricing from '@/modules/common/common/models/MID/RaterPricing'
import type BaseRaterStore from '@/modules/common/rater/models/APP/BaseRaterStore'
import { DXPConfigPathEnum } from '@/modules/core/models/enums'
import ConsoleError from '@/modules/core/models/errors/ConsoleError'
import GTMEventType from '@/modules/core/models/GTMEventType'
import TagAlertValue from '@/modules/core/models/TagAlertValue'
import { useGTMStore } from '@/modules/core/stores/gtmStore'
import { useSharedGtmDataStore } from '@/modules/core/stores/sharedGtmDataStore'
import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
import HealthCommonMapper from '@/modules/health/common/mappers'
import HealthConfigurations from '@/modules/health/common/models/APP/HealthConfigurations'
import HealthInsurance from '@/modules/health/common/models/APP/HealthInsurance'
import PAGES from '@/modules/health/rater/constants/pages'
import HealthRaterAnswers from '@/modules/health/rater/models/APP/HealthRaterAnswers'
import PAGES_PYMES from '@/modules/pymes/health/rater/constants/pages'
import HealthPymesConfiguration from '../../common/models/APP/HealthPymesConfiguration'

type HealthBranchConfiguration = HealthConfigurations | HealthPymesConfiguration

export const useHealthStore = defineStore('raterHealth', () => {
  const errorManager = useErrorManagerComposable()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()
  const commonStore = useCommonStore()
  const router = useRouter()
  const { pushPreservingMCode } = useNavigation()
  const route = useRoute()
  const { t } = useI18n()
  const supportModalStore = useSupportModalStore()
  const gmtStore = useGTMStore()
  const sharedGtmDataStore = useSharedGtmDataStore()

  const globalConfig: Ref<HealthBranchConfiguration> = ref(new HealthConfigurations())
  let defaultHiringAges: {
    holderInsured: { min: number; max: number }
    holderNotInsured: { min: number; max: number }
    insured: { min: number; max: number }
  } = {
    holderInsured: { min: 0, max: 0 },
    holderNotInsured: { min: 0, max: 0 },
    insured: { min: 0, max: 0 }
  }
  const raterAnswers = ref(new HealthRaterAnswers())
  const insuranceProposals = ref<HealthInsurance[]>([])
  const coverages = ref<CoverageBasic[]>([])

  const pages = ref(getPages())

  function isInternationalConfig(
    config: HealthBranchConfiguration
  ): config is HealthConfigurations {
    return !config.isPymes
  }

  watch(
    () => raterAnswers.value.insuranceType,
    (insuranceType) => {
      if (!insuranceType) return

      const updatedPages = getPages()

      if (insuranceType === '3') {
        updatedPages.MoreInfoForm.next = ['CoveragesForm']
        updatedPages.CoveragesForm.next = []
        globalConfig.value.startDateHint = new TagAlertValue({
          label: t('copy_international_start-date-hint', {
            days: globalConfig.value.maxDays
          }),
          icon: 'icon-informe-medico'
        })
      } else {
        updatedPages.MoreInfoForm.next = []
        raterAnswers.value.coverage = null
        raterAnswers.value.abroadOption = null
        globalConfig.value.hiringMaxAges = defaultHiringAges
        globalConfig.value.startDateHint = null
      }

      pages.value = updatedPages
    }
  )

  watch(
    () => raterAnswers.value.abroadOption,
    (abroadOption) => {
      if (!abroadOption) return

      if (!isInternationalConfig(globalConfig.value)) {
        return
      }

      if (abroadOption === globalConfig.value.stayAbroadTypes?.work.id) {
        return (globalConfig.value.hiringMaxAges =
          globalConfig.value.hiringMaxAgesInternationalWork)
      }

      if (abroadOption === globalConfig.value.stayAbroadTypes?.study.id) {
        return (globalConfig.value.hiringMaxAges =
          globalConfig.value.hiringMaxAgesInternationalStudy)
      }

      return (globalConfig.value.hiringMaxAges = defaultHiringAges)
    }
  )

  function getPages(): Record<string, FormPage> {
    return globalConfig.value.isPymes ? PAGES_PYMES : PAGES
  }

  function getBranchConfiguration(): HealthBranchConfiguration {
    return globalConfig.value
  }

  function getInsuranceProposals() {
    return insuranceProposals.value
  }

  function clearRaterAnswers(): void {
    raterAnswers.value = new HealthRaterAnswers()
  }

  function getRateFormAnswers() {
    return raterAnswers.value as HealthRaterAnswers
  }

  function setRateFormAnswers(answers: Partial<HealthRaterAnswers>) {
    if (!answers) return

    raterAnswers.value.updateAttributes(answers)
  }

  function getMostRelevantProducts(insurances: HealthInsurance[]) {
    const sortedItems = insurances.sort((a, b) => a.order - b.order)
    const copayType = raterAnswers.value.copayType

    // 2 o menos => DXP
    if (sortedItems.length <= 2) {
      return sortedItems.slice(0, 2)
    }

    // Sin seleccionar copago => DXP
    if (copayType === globalConfig.value?.copaymentTypes.none.id) {
      return sortedItems.slice(0, 2)
    }

    // Con copago y sin coincidencias => DXP
    const matchingCopay1 = sortedItems.find((s) => s.copayType === copayType)
    if (!matchingCopay1) {
      return sortedItems.slice(0, 2)
    }

    // Con copago y con una coincidencia => coincidencia + DXP (distinto al seleccionado)
    let matchingCopay2 = sortedItems.find(
      (s) => s.copayType === copayType && s.productCode !== matchingCopay1.productCode
    )
    if (!matchingCopay2) {
      matchingCopay2 = sortedItems.find((s) => s.order !== matchingCopay1.order)
      return [matchingCopay1, matchingCopay2!]
    }

    // Con copago y con dos coincidencias => coincidencias
    return [matchingCopay1, matchingCopay2]
  }

  async function loadPymesConfiguration() {
    const { error, data } = await DXPService.post(ENDPOINTS.DXP_GET_BRANCH_CONFIG, {
      path: DXPConfigPathEnum.PYMES_HEALTH
    }) //TODO: Use real path
    if (error) {
      throw new ServiceError(data)
        .setDisplayType(ErrorDisplayTypeEnum.MODAL)
        .setDisplayType(ErrorDisplayTypeEnum.MODAL)
        .setModalDescription('copy_error_service_description')
        .setButtons([
          new ModalButton({
            text: 'header.help-text',
            icon: 'icon-message-text-01',
            type: ButtonTypeEnum.OUTLINE,
            onclick: () => {
              gmtStore.sendEvent({
                event: GTMEventType.CALLMEBACK_BTN,
                click_name: 'support-modal-btn',
                click_id: 'support-modal-btn',
                btn_label: t('header.help-text'),
                value: sharedGtmDataStore.policyValue
              })
              supportModalStore.setNavTo('exit')
              supportModalStore.setOpenModal(true)
            }
          }),
          new ModalButton({
            text: 'copy_button_return',
            navTo: 'exit',
            type: ButtonTypeEnum.OUTLINE
          })
        ])
    }
    return data
  }

  async function loadHealthConfiguration() {
    const { error, data } = await DXPService.get(ENDPOINTS.DXP_GET_BRANCH_HEALTH)
    if (error) {
      throw new ServiceError(data)
        .setDisplayType(ErrorDisplayTypeEnum.MODAL)
        .setDisplayType(ErrorDisplayTypeEnum.MODAL)
        .setModalDescription('copy_error_service_description')
        .setButtons([
          new ModalButton({
            text: 'header.help-text',
            icon: 'icon-message-text-01',
            type: ButtonTypeEnum.OUTLINE,
            onclick: () => {
              gmtStore.sendEvent({
                event: GTMEventType.CALLMEBACK_BTN,
                click_name: 'support-modal-btn',
                click_id: 'support-modal-btn',
                btn_label: t('header.help-text'),
                value: sharedGtmDataStore.policyValue
              })
              supportModalStore.setNavTo('exit')
              supportModalStore.setOpenModal(true)
            }
          }),
          new ModalButton({
            text: 'copy_button_return',
            navTo: 'exit',
            type: ButtonTypeEnum.OUTLINE
          })
        ])
    }
    return data
  }

  async function loadConfiguration() {
    try {
      const isPymes = route.meta.isPymes ?? false
      const data = isPymes ? await loadPymesConfiguration() : await loadHealthConfiguration()
      globalConfig.value = isPymes
        ? new HealthPymesConfiguration({ ...data, defaultHiringMaxAges: data.hiringMaxAges })
        : new HealthConfigurations({
            ...data,
            defaultHiringMaxAges: data.hiringMaxAges
          })
      defaultHiringAges = JSON.parse(JSON.stringify(data.hiringMaxAges))
      raterAnswers.value.businessLine = globalConfig.value.businessLineId
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function loadMaxAgeByBusinessLine({
    insuranceType = null,
    abroadOption = null
  }: {
    insuranceType: string | null
    abroadOption: string | null
  }) {
    try {
      const innerInsurance = insuranceType ?? raterAnswers.value.insuranceType ?? null
      const innerAbroadOption = abroadOption ?? raterAnswers.value.abroadOption ?? null
      const endpoint = ENDPOINTS.DXP_GET_MAX_AGE_BY_BUSINESS_LINE
      const request = HealthCommonMapper.GetMaxAgeMapper.toRequest(
        globalConfig.value.businessLineId,
        innerInsurance ?? '',
        innerAbroadOption ?? ''
      )
      const { error, data } = await DXPService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }
      if (data && !Array.isArray(data) && globalConfig.value.hiringMaxAges) {
        globalConfig.value.hiringMaxAges.holderInsured.max = data.hiringMaxAge
        globalConfig.value.hiringMaxAges.insured.max = data.hiringMaxAge
      } else {
        if (innerInsurance === '3' && isInternationalConfig(globalConfig.value)) {
          if (innerAbroadOption === globalConfig.value.stayAbroadTypes?.work.id) {
            globalConfig.value.hiringMaxAges = globalConfig.value.hiringMaxAgesInternationalWork
          } else if (innerAbroadOption === globalConfig.value.stayAbroadTypes?.study.id) {
            globalConfig.value.hiringMaxAges = globalConfig.value.hiringMaxAgesInternationalStudy
          } else {
            globalConfig.value.hiringMaxAges = globalConfig.value.defaultHiringMaxAges
          }
        } else {
          globalConfig.value.hiringMaxAges = globalConfig.value.defaultHiringMaxAges
        }
      }
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleError(error)
    }
  }

  async function sendRate(
    options: {
      preventRouting?: boolean
      answers?: HealthRaterAnswers | undefined
      externalErrorHandling?: boolean
      checkLocation?: string
    } = {}
  ) {
    options.preventRouting = Boolean(options.preventRouting)
    options.externalErrorHandling = Boolean(options.externalErrorHandling)
    if (!options.checkLocation) {
      options.checkLocation = location.pathname
    }

    try {
      const loadingTexts = ['common.loading.processing']
      if (!options.preventRouting) {
        loadingTexts.push('common.loading.looking-for-best-products')
      }
      loadingStore.startLoading(...loadingTexts)

      const endpoint = ENDPOINTS.RATER_CALCULATE_PRICE
      const answers = options.answers ?? (raterAnswers.value as HealthRaterAnswers)

      const commonConfig = commonStore.getCommonConfigs()
      if (UserRoleEnum.HOLDER_NOT_INSURED === answers.holder.role || globalConfig.value.isPymes) {
        answers.holder!.addressDetails.postalCode ||= commonConfig?.defaultPostalCode ?? null
      } else {
        answers.holder!.addressDetails.postalCode = answers.policyHolderPostal
      }

      const request = HealthCommonMapper.GetPricingMapper.toRequest(
        answers,
        true,
        commonStore.infoMediator?.id
      )
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data).setDisplayType(ErrorDisplayTypeEnum.MODAL)
      }

      if (options.answers) {
        raterAnswers.value.updateAttributes(options.answers)
      }

      const response = new RaterPriceResponse(data)
      if (response.listPricing.length === 0) {
        throw new ServiceSchemaError()
          .setTitle('copy_rater-empty-error-modal_title')
          .setModalDescription('copy_rater-empty-error-modal_description')
          .setButtons([
            new ModalButton({
              text: 'components.InsuredSubheaderComponent.button'
            }),
            new ModalButton({
              text: 'copy_button_init_return',
              navTo: `rater-nav${globalConfig.value.isPymes ? '-pymes' : ''}`,
              type: ButtonTypeEnum.OUTLINE
            })
          ])
      }

      setRateFormAnswers({
        quotationId: response.quotationId,
        businessLine: response.listPricing[0].businessLine
      })
      const products = data.listPricing.map((l: RaterPricing) => {
        return new ProductId({
          productCode: l.productCode,
          clientCode: l.clientCode,
          coverageCode: raterAnswers.value.coverage ?? undefined
        })
      })
      const dxpData = await _fetchDXPInsurances(products)
      insuranceProposals.value = _loadCompleteSheetProducts(dxpData, data.listPricing)

      if (insuranceProposals.value.length === 0) {
        const modalButtons = [
          new ModalButton({
            text: 'components.InsuredSubheaderComponent.button'
          }),
          new ModalButton({
            text: 'copy_button_init_return',
            navTo: `rater-nav${globalConfig.value.isPymes ? '-pymes' : ''}`,
            type: ButtonTypeEnum.OUTLINE
          })
        ]
        throw new ServiceSchemaError()
          .setTitle('copy_rater-empty-error-modal_title')
          .setModalDescription('copy_rater-empty-error-modal_description')
          .setButtons(modalButtons)
      }

      if (!options.preventRouting && options.checkLocation === location.pathname) {
        // check pathname to prevent to push a route when user clicks to go to back or other stuff
        await pushPreservingMCode(router, {
          name: `${globalConfig.value.isPymes ? 'pymes-' : ''}health-insurance-proposals`
        })
      }
    } catch (error) {
      console.error(error)
      if (options.externalErrorHandling) {
        throw error
      }

      errorManager.handleError(error)
    } finally {
      loadingStore.stopLoading()
    }
  }

  //** PRIVATE SUPPORT METHODS */
  async function _fetchDXPInsurances(products: ProductId[]) {
    const endpoint = ENDPOINTS.DXP_GET_PRODUCT
    const request = HealthCommonMapper.GetProductsMapper.toRequest(products)
    const { error, data } = await DXPService.post(endpoint, request)
    if (error) throw new ServiceError(data)

    return data.map((d: any) => new GetProductResponse(d).product)
  }

  function _loadCompleteSheetProducts(
    DXPProducts: DXPProduct[],
    MIDProducts: RaterPricing[]
  ): HealthInsurance[] {
    const products: HealthInsurance[] = []
    DXPProducts.forEach((DXPProduct) => {
      const MIDProduct = MIDProducts.find((p) => {
        return String(DXPProduct.productId) === String(p.productCode)
      })

      if (MIDProduct) {
        const copaymentTypesOptions = globalConfig.value.copaymentTypesOptions
        const insurance = HealthCommonMapper.GetProductsMapper.fromResponse(
          DXPProduct,
          MIDProduct,
          copaymentTypesOptions
        )
        products.push(insurance)
      }
    })

    return products
  }

  async function hireProduct(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: HealthInsurance,
    role: UserRoleEnum
  ): Promise<{ valid: boolean }> {
    let valid = true

    try {
      loadingStore.startLoading('Procesando...')

      const { error, data } = await _saveQuotation(
        quotationId,
        startDate,
        billingCycle,
        product,
        role
      )
      if (error) throw new ServiceError(data)
    } catch (error) {
      console.error(error)
      valid = false
    } finally {
      loadingStore.stopLoading()
    }

    return { valid }
  }

  async function loadSpecificProduct(productCode: string, clientCode: string) {
    try {
      const dxpData = await _fetchDXPInsurances([{ productCode, clientCode }])
      if (!dxpData.length) {
        throw new ConsoleError({
          message: `DXP Error: No se ha encontrado la ficha para el producto ${productCode}`
        })
      }

      const DXPProduct = dxpData[0]
      const copaymentTypesOptions = globalConfig.value.copaymentTypesOptions
      const insurance = HealthCommonMapper.GetProductsMapper.fromResponse(
        DXPProduct,
        {},
        copaymentTypesOptions
      )

      if (DXPProduct.hiringMaxAge && globalConfig.value.hiringMaxAges) {
        globalConfig.value.hiringMaxAges.holderInsured.max = DXPProduct.hiringMaxAge
        globalConfig.value.hiringMaxAges.insured.max = DXPProduct.hiringMaxAge
      }

      //TODO: WAIT TO FIX THIS
      let abroadOption = null
      let insuranceType = null
      if (productCode === '101064' && isInternationalConfig(globalConfig.value)) {
        insuranceType = '3'
        abroadOption = globalConfig.value.stayAbroadTypes?.work.id
        insurance.title = 'ASISA Internacional'
      }
      if (productCode === '101063' && isInternationalConfig(globalConfig.value)) {
        insuranceType = '3'
        abroadOption = globalConfig.value.stayAbroadTypes?.study.id
        insurance.title = 'ASISA Internacional Estudiantes'
      }
      const insured = new Insured({
        role: UserRoleEnum.HOLDER_INSURED
      })
      raterAnswers.value = new HealthRaterAnswers({
        product: insurance,
        businessLine: globalConfig.value.businessLineId,
        holder: insured,
        insureds: [insured],
        insuranceType,
        abroadOption
      })
    } catch (error) {
      errorManager.handleError(error)
      throw error
    }
  }

  async function _saveQuotation(
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: Insurance,
    holderRole: UserRoleEnum
  ) {
    if (!quotationId) throw new Error('Quotation id is required')

    const endpoint = ENDPOINTS.SAVE_QUOTATION
    const request = CommonMapper.SaveQuotationMapper.toRequest(
      quotationId,
      startDate,
      billingCycle,
      product,
      holderRole
    )
    const { error, data } = await MiddlewareService.post(endpoint, request)
    return { error, data }
  }

  async function sendBudget(
    quotationNumber: string,
    email: string,
    privacyPolicyConsent: boolean,
    quotationId: string | null,
    startDate: Date,
    billingCycle: string,
    product: HealthInsurance,
    holderRole: UserRoleEnum
  ) {
    try {
      loadingStore.startLoading('Procesando...')

      const quotationResult = await _saveQuotation(
        quotationId,
        startDate,
        billingCycle,
        product,
        holderRole
      )
      if (quotationResult.error) {
        throw new ServiceError(quotationResult.data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle(t('copy_budget-service_error'))
      }

      const endpoint = ENDPOINTS.SEND_BUDGET
      const request = { quotationNumber, to: email, privacyPolicyConsent }
      const { error, data } = await MiddlewareService.post(endpoint, request)
      if (error) {
        throw new ServiceError(data)
          .setDisplayType(ErrorDisplayTypeEnum.TOAST_IN_MODAL)
          .setTitle(t('copy_budget-service_error'))
      }

      toastStore.successToast('copy_budget-service_success')
    } catch (error) {
      errorManager.handleError(error)
      throw error
    } finally {
      loadingStore.stopLoading()
    }
  }

  async function loadCoverages({
    preventCache = false
  }: {
    insuranceType?: string | undefined
    clientCode?: string | undefined
    productCode?: string | undefined
    preventCache?: boolean
  }): Promise<CoverageBasic[]> {
    if (coverages.value.length && !preventCache) {
      return coverages.value
    }

    const { businessLineId } = globalConfig.value
    coverages.value = await commonStore.loadCoverages({
      businessLine: businessLineId,
      insuranceType: '3'
    })

    return coverages.value
  }

  const store: BaseRaterStore<
    HealthInsurance,
    HealthRaterAnswers,
    HealthConfigurations | HealthPymesConfiguration
  > & {
    loadCoverages: ({ preventCache }: { preventCache?: boolean }) => Promise<CoverageBasic[]>
    loadMaxAgeByBusinessLine: ({
      insuranceType,
      abroadOption
    }: {
      insuranceType: string | null
      abroadOption: string | null
    }) => Promise<any>
  } = {
    pages: globalConfig.value.isPymes ? PAGES_PYMES : PAGES,
    getPages,
    getBranchConfiguration,
    clearRaterAnswers,
    getInsuranceProposals,
    setRateFormAnswers,
    getRateFormAnswers,
    getMostRelevantProducts,

    loadConfiguration,
    loadSpecificProduct,
    hireProduct,
    sendRate,
    sendBudget,

    loadCoverages,
    loadMaxAgeByBusinessLine
  }

  return store
})
